import { faEnvelope, faHashtag } from "@fortawesome/pro-light-svg-icons"
import {
  AuthCodeType,
  Button,
  Checkbox,
  JoonUIColor,
  Loader,
  ModalHeader,
  SegmentedControl,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { Modal } from "@joonapp/web-shared"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"

import { useLinkTherapistModalStore } from "./useLinkTherapistModalStore"
import {
  PhoneNumberModalType,
  useAddPhoneNumberModalStore,
} from "../../../components/modals/addPhoneNumberModal/useAddPhoneNumberModalStore"
import { QUERY_KEYS } from "../../../constants"
import { useFamilyQuery, useUserQuery } from "../../../networking/queries"
import { sendTherapistInvitation } from "../../../networking/user"
import { FamilyQuerySelect } from "../../../types"
import { createJoonAPIClient } from "../../../util/joon-api"
import { queryClient } from "../../../util/queryClient"
import { useRouter } from "../../../util/router"
import { localStorageItems } from "../../../util/storage"

const LinkTherapistModal = () => {
  const [email, setEmail] = useState<string>("")
  const [code, setCode] = useState<string>("")
  const [error, setError] = useState<string | null>(null)
  const [isValidatingCode, setIsValidatingCode] = useState<boolean>(false)
  const [selectedChildren, setSelectedChildren] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { isOpen, onClose, onOpen, hasCode, setHasCode } =
    useLinkTherapistModalStore()
  const { onOpen: openAddPhoneNumberModal } = useAddPhoneNumberModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)

  const router = useRouter()
  const { user } = useUserQuery()
  const { t } = useTranslation()

  useEffect(() => {
    if (router.query.therapist_code) {
      localStorage.setItem(
        localStorageItems.therapistCode,
        router.query.therapist_code
      )
      router.replace(router.pathname)
    }
    if (!!user) {
      const code = localStorage.getItem(localStorageItems.therapistCode)
      // currently will always open until user confirms therapist code
      // maybe need to change to only clear code when not on paywall or therapists tab
      if (code) {
        onOpen()
        setHasCode(true)
        setCode(code)
      }
    }
  }, [router, user, onOpen, setHasCode, setCode])

  useEffect(() => {
    if (code.length === 8) validateCode()
    else setError("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  const sendInvite = async () => {
    setIsLoading(true)
    try {
      await sendTherapistInvitation(email, selectedChildren)
      await queryClient.invalidateQueries([QUERY_KEYS.THERAPIST_INVITES])
      onClose()
      toast.success(t("LT_TOAST_INVITE_SENT"))
      localStorage.removeItem(localStorageItems.showABAInviteSection)
      setEmail("")
    } catch (error) {
      console.error(error)
    }
    setIsLoading(false)
  }

  const toggleChild = (childId: number) => {
    if (selectedChildren.includes(childId)) {
      setSelectedChildren(selectedChildren.filter((id) => id !== childId))
    } else {
      setSelectedChildren([...selectedChildren, childId])
    }
  }

  const validateCode = async () => {
    if (code.length !== 8) return setError(t("LT_ERR_CODE_LENGTH"))

    try {
      const API = createJoonAPIClient()
      setIsValidatingCode(true)
      const response = await API.get("api/auth-codes/validate/", {
        params: {
          code: code.toUpperCase(),
          type: AuthCodeType.ADMIT_PATIENT,
        },
      })
      if (!response.data.group.profiles) return setError(t("LT_ERR_GENERIC"))
      setError(null)
    } catch (error) {
      setError(t("LT_ERR_INVALID"))
    }
    setIsValidatingCode(false)
  }

  const linkChildren = async () => {
    const API = createJoonAPIClient()

    const linkChildrenPromises = selectedChildren.map((child) =>
      API.post("api/practices/admit-patient/", {
        code: code.toUpperCase(),
        user_id: child,
      })
    )
    try {
      setIsLoading(true)
      localStorage.removeItem(localStorageItems.therapistCode)
      const responses = await Promise.all(linkChildrenPromises)
      queryClient.invalidateQueries([QUERY_KEYS.THERAPISTS])
      queryClient.invalidateQueries([QUERY_KEYS.CARE_TEAM])
      queryClient.invalidateQueries([QUERY_KEYS.USER])
      setError(null)
      setCode("")
      localStorage.setItem(localStorageItems.needsToSeeTherapistTour, "true")
      if (router.pathname.includes("/paywall")) {
        localStorage.setItem(localStorageItems.needsToSeeWelcomeRewards, "true")
        localStorage.setItem(localStorageItems.needsToSeeWelcomeQuests, "true")
      }
      onClose()
      toast(
        <div>
          <Typography
            variant="bodySmallBold"
            color={JoonUIColor.text.inverted}
            textAlign="center"
          >
            {t("LT_TOAST_LINKED_TITLE")}
          </Typography>
          <Typography
            variant="bodyXSmall"
            color={JoonUIColor.text.inverted}
            textAlign="center"
          >
            {t("LT_TOAST_LINKED_DESC")}
          </Typography>
        </div>
      )
      if (!user?.phone_number || user.phone_number.length < 12) {
        openAddPhoneNumberModal(PhoneNumberModalType.THERAPIST)
      } else if (router.pathname.includes("/paywall")) router.push("/me")
      return responses
    } catch (error) {
      setError(t("LT_ERR_GENERIC"))
      throw new Error("Failed to link children")
    } finally {
      setIsLoading(false)
    }
  }

  const isButtonDisabled = useMemo(() => {
    if (selectedChildren.length === 0) return true
    if (isValidatingCode) return true

    if (hasCode) {
      if (code.length !== 8) return true
      if (error) return true
    } else {
      if (email === "") return true
    }
    return false
  }, [code, email, error, hasCode, isValidatingCode, selectedChildren])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      animate
      mobileFull
      style={{ width: "min(450px, 100vw)" }}
    >
      <ModalHeader title={t("LT_TITLE")} onClose={onClose} showBorderBottom />
      <div className="flex flex-col p-6 pt-4 gap-6 overflow-y-auto">
        <div className="flex flex-col gap-2">
          <Typography variant="body">{t("LT_HAS_CODE_Q")}</Typography>
          <SegmentedControl
            options={[
              { label: t("YES"), value: "yes" },
              { label: t("NO"), value: "no" },
            ]}
            value={hasCode ? "yes" : "no"}
            fullWidth
            setValue={(value) => setHasCode(value === "yes")}
          />
        </div>
        {hasCode ? (
          <div className="flex flex-col gap-1">
            <Typography variant="body">{t("LT_THERAPIST_CODE")}</Typography>
            <div className="relative">
              <TextInput
                placeholder="ABCD1234"
                fullWidth
                maxLength={8}
                value={code}
                icon={faHashtag}
                onChange={(e) => setCode(e.target.value)}
                name="code"
              />
              {isValidatingCode && (
                <div className="absolute top-[15px] right-[12px]">
                  <Loader
                    thickness={2}
                    size={16}
                    color={JoonUIColor.icon.neutral}
                  />
                </div>
              )}
            </div>
            {error && (
              <Typography
                variant="bodySmall"
                color={JoonUIColor.semantic.alert}
              >
                {error}
              </Typography>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <Typography variant="body">{t("LT_THERAPIST_EMAIL")}</Typography>
            <TextInput
              placeholder={t("LT_EMAIL_PLACEHOLDER")}
              fullWidth
              value={email}
              icon={faEnvelope}
              onChange={(e) => setEmail(e.target.value)}
              name="email"
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Typography variant="body">{t("LT_WHOSE")}</Typography>
          <div className="flex flex-col border border-borderPrimary rounded-md">
            {children?.map((child, index) => (
              <button key={index} onClick={() => toggleChild(child.user.id)}>
                <div
                  className={`flex flex-row items-center gap-2 p-3 justify-between ${
                    index !== children.length - 1
                      ? "border-b border-borderPrimary"
                      : ""
                  }`}
                  key={index}
                >
                  <Typography variant="body">{child.user.name}</Typography>
                  <Checkbox
                    selected={selectedChildren.includes(child.user.id)}
                    onChange={() => {}}
                    name={child.user.name}
                    hideBorder
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <Typography variant="bodySmall">{t("LT_DATA_SHARED")}</Typography>
          <Typography variant="bodySmall">
            <ul>
              <li>{t("LT_DATA_1")}</li>
              <li>{t("LT_DATA_2")}</li>
              <li>{t("LT_DATA_3")}</li>
            </ul>
          </Typography>
        </div>
      </div>
      <div className="px-6 pb-4 pt-2 border-t border-borderPrimary">
        <Button
          text={hasCode ? t("LT_LINK_BTN") : t("ME_ABA_INVITE")}
          onClick={hasCode ? linkChildren : sendInvite}
          isLoading={isLoading}
          disabled={isButtonDisabled}
          fullWidth
        />
      </div>
    </Modal>
  )
}

export default LinkTherapistModal
